import type { SxProps } from '@mui/material';
import React, { useState } from 'react';

import { useGoToActiveCompetition } from '@/competitions/_common/hooks/useGoToActiveCompetition.ts';
import { getRegisterSourceData } from '@/lib/analytics/registerSource.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWithConfig } from '@/lib/api/requests.ts';
import { mergeSx } from '@/lib/utils.ts';
import { Button } from '@/ui/Button.tsx';

import useBrainSession from '../../../../../../services/BrainSession/useBrainSession.ts';
import { CardDialog } from '../../../chat-answers/_common/card-dialog.tsx';

import { useHasEnded } from './useHasEnded.ts';

export type CTAProps = {
  sx?: SxProps;
  onClick?: () => void;
  activeCompetition: CompetitionWithConfig | undefined;
  displayWarningWhenKIn: string[] | undefined;
};

export const CTA: React.FC<CTAProps> = ({
  sx,
  onClick,
  activeCompetition,
  displayWarningWhenKIn,
}) => {
  const { toggleChat } = useBrainSession();
  const ended = useHasEnded();
  const goToActiveCompetition = useGoToActiveCompetition(activeCompetition);
  const checkShouldAsk = () => {
    if (!displayWarningWhenKIn) {
      return false;
    }
    // getRegisterSourceData reads from window/cookies, so it must only be
    // called in event handlers (not during render) to stay SSR-safe.
    return displayWarningWhenKIn.includes(getRegisterSourceData().params.k);
  };
  const [isOpen, setIsOpen] = useState(false);

  if (ended) {
    return (
      <Button
        color="orange"
        sx={mergeSx({ mx: 'auto' }, sx)}
        onClick={() => {
          onClick?.();
          void goToActiveCompetition();
        }}
      >
        Se den nye trekningen
      </Button>
    );
  }

  return (
    <>
      <Button
        color="orange"
        sx={mergeSx({ mx: 'auto' }, sx)}
        onClick={() => {
          onClick?.();
          if (checkShouldAsk()) {
            sendUserPortalEvent('apr26_conditions_notice_displayed');
            setIsOpen(true);
            return;
          }
          toggleChat({
            goal: 'april_2026_campaign',
            step: 'init',
          });
        }}
      >
        Gjett og delta
      </Button>

      <CardDialog.Root open={isOpen} onOpenChange={setIsOpen}>
        <CardDialog.Content>
          <CardDialog.Card>
            <CardDialog.Title className="mb-2 typography-v1-h3">
              Har du rentebærende forbruks- eller kredittkortgjeld?
            </CardDialog.Title>

            <p className="mb-6 typography-v1-body3">
              For å delta i konkurransen må man søke om lavere rente på eksisterende forbruks- og
              kredittkortgjeld.
            </p>

            <div className="flex flex-col gap-4">
              <Button
                color="orange"
                onClick={() => {
                  setIsOpen(false);
                  sendUserPortalEvent('apr26_conditions_notice_submitted');
                  toggleChat({
                    goal: 'april_2026_campaign',
                    step: 'init',
                  });
                }}
              >
                Ja, jeg har det. Fortsett
              </Button>
              <Button
                color="transparent"
                onClick={() => {
                  sendUserPortalEvent('apr26_conditions_notice_closed');
                  setIsOpen(false);
                }}
              >
                Lukk
              </Button>
            </div>
          </CardDialog.Card>
        </CardDialog.Content>
      </CardDialog.Root>
    </>
  );
};
